// Chakra imports
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  Card,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Services
import tutorsService from 'services/tutorsService';
import { formatDate, formatStatus } from 'utils/format';

// Icons
import { MdArrowBack, MdPerson, MdEmail, MdPhone, MdSchool, MdCalendarToday, MdCheckCircle, MdWork, MdEdit, MdDelete, MdThumbUp, MdThumbDown } from 'react-icons/md';

export default function ViewTutor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

  // State
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    current_year: '',
    academic_field: '',
    department_of_choice: '',
    experience_at_mia: '',
    why_choose_you: '',
  });
  const [approvalNotes, setApprovalNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isApproveOpen, onOpen: onApproveOpen, onClose: onApproveClose } = useDisclosure();
  const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch tutor data
  useEffect(() => {
    const fetchTutor = async () => {
      if (!id) {
        setError('Tutor ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await tutorsService.getTutor(id);

        if (result.success && result.data) {
          setTutor(result.data);
        } else {
          setError(result.error || 'Failed to load tutor');
          toast({
            title: 'Error',
            description: result.error || 'Failed to load tutor',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error('Error fetching tutor:', err);
        setError('Failed to load tutor. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load tutor. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTutor();
  }, [id, toast]);

  // Initialize form data when tutor is loaded
  useEffect(() => {
    if (tutor) {
      setFormData({
        name: tutor.name || '',
        email: tutor.email || '',
        phone: tutor.phone || '',
        current_year: tutor.current_year || '',
        academic_field: tutor.academic_field || '',
        department_of_choice: tutor.department_of_choice || '',
        experience_at_mia: tutor.experience_at_mia || '',
        why_choose_you: tutor.why_choose_you || '',
      });
    }
  }, [tutor]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle update tutor
  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const updateData = { ...formData };

      // Remove empty fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const result = await tutorsService.updateTutorPartial(id, updateData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tutor updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditClose();
        // Refresh tutor data
        const fetchResult = await tutorsService.getTutor(id);
        if (fetchResult.success && fetchResult.data) {
          setTutor(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update tutor',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Update error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update tutor. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete tutor
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await tutorsService.deleteTutor(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tutor deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to tutors list
        navigate('/admin/tutors');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete tutor',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete tutor. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle approve tutor
  const handleApprove = async () => {
    setApproving(true);
    try {
      const approvalData = approvalNotes.trim() ? { approval_notes: approvalNotes.trim() } : {};
      const result = await tutorsService.approveTutor(id, approvalData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tutor approved successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onApproveClose();
        setApprovalNotes('');
        // Refresh tutor data
        const fetchResult = await tutorsService.getTutor(id);
        if (fetchResult.success && fetchResult.data) {
          setTutor(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to approve tutor',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Approve error:', err);
      toast({
        title: 'Error',
        description: 'Failed to approve tutor. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setApproving(false);
    }
  };

  // Handle reject tutor
  const handleReject = async () => {
    setRejecting(true);
    try {
      const rejectionData = approvalNotes.trim() ? { approval_notes: approvalNotes.trim() } : {};
      const result = await tutorsService.rejectTutor(id, rejectionData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tutor rejected successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onRejectClose();
        setApprovalNotes('');
        // Refresh tutor data
        const fetchResult = await tutorsService.getTutor(id);
        if (fetchResult.success && fetchResult.data) {
          setTutor(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to reject tutor',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Reject error:', err);
      toast({
        title: 'Error',
        description: 'Failed to reject tutor. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Center minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Center>
      </Box>
    );
  }

  if (error || !tutor) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card p='40px' bg={cardBg}>
          <Flex direction='column' align='center' gap='20px'>
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              {error || 'Tutor not found'}
            </Text>
            <Button onClick={() => navigate(-1)} leftIcon={<Icon as={MdArrowBack} />}>
              Go Back
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  const statusColorScheme = 
    tutor.approve === 'approved' ? 'green' : 
    tutor.approve === 'pending' ? 'orange' : 
    tutor.approve === 'rejected' ? 'red' : 'brand';

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Back, Edit, Delete, Approve, and Reject Buttons */}
        <Flex justify='space-between' align='center' mb='10px' flexWrap='wrap' gap='10px'>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<Icon as={MdArrowBack} />}
            variant='ghost'>
            Go Back
          </Button>
          <Flex gap='10px' flexWrap='wrap'>
            <Button
              onClick={onEditOpen}
              leftIcon={<Icon as={MdEdit} />}
              colorScheme='blue'
              variant='solid'>
              Edit Tutor
            </Button>
            {tutor.approve === 'pending' && (
              <>
                <Button
                  onClick={onApproveOpen}
                  leftIcon={<Icon as={MdThumbUp} />}
                  colorScheme='green'
                  variant='solid'>
                  Approve
                </Button>
                <Button
                  onClick={onRejectOpen}
                  leftIcon={<Icon as={MdThumbDown} />}
                  colorScheme='red'
                  variant='solid'>
                  Reject
                </Button>
              </>
            )}
            <Button
              onClick={onDeleteOpen}
              leftIcon={<Icon as={MdDelete} />}
              colorScheme='red'
              variant='solid'>
              Delete Tutor
            </Button>
          </Flex>
        </Flex>

        {/* Tutor Header */}
        <Card p='30px' bg={cardBg}>
          <Flex direction='column' gap='20px'>
            <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
              <Flex direction='column'>
                <Text color={textColor} fontSize='2xl' fontWeight='700' mb='5px'>
                  Tutor Details
                </Text>
                <Text color={textColorSecondary} fontSize='sm'>
                  ID: {tutor.id}
                </Text>
              </Flex>
              <Badge 
                colorScheme={statusColorScheme} 
                bg={bgStatus} 
                fontSize="md" 
                fontWeight="700" 
                px="15px" 
                py="5px"
              >
                {formatStatus(tutor.approve)}
              </Badge>
            </Flex>
            <Divider borderColor={borderColor} />
          </Flex>
        </Card>

        {/* Personal Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPerson} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Personal Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Full Name
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.name}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdEmail} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Email
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.email}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdPhone} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Phone
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600' fontFamily="mono">
                {tutor.phone}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Academic Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdSchool} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Academic Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Current Year
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.current_year}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Academic Field
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.academic_field}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Department of Choice
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.department_of_choice}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdWork} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Experience at MIA
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tutor.experience_at_mia || 'N/A'}
              </Text>
            </Box>
            {tutor.why_choose_you && (
              <Box gridColumn={{ base: '1', md: '1 / 3' }}>
                <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                  Why Choose You
                </Text>
                <Text color={textColor} fontSize='md' fontWeight='500' whiteSpace="pre-wrap">
                  {tutor.why_choose_you}
                </Text>
              </Box>
            )}
          </SimpleGrid>
        </Card>

        {/* Approval Information */}
        {tutor.approve === 'approved' && (
          <Card p='30px' bg={cardBg}>
            <Flex align='center' gap='10px' mb='20px'>
              <Icon as={MdCheckCircle} color='green.500' fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Approval Information
              </Text>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
              {tutor.approved_by && (
                <Box>
                  <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                    Approved By
                  </Text>
                  <Text color={textColor} fontSize='md' fontWeight='600'>
                    {tutor.approved_by}
                  </Text>
                </Box>
              )}
              {tutor.approval_date && (
                <Box>
                  <Flex align='center' gap='5px' mb='5px'>
                    <Icon as={MdCalendarToday} color={textColorSecondary} fontSize='sm' />
                    <Text color={textColorSecondary} fontSize='sm'>
                      Approval Date
                    </Text>
                  </Flex>
                  <Text color={textColor} fontSize='md' fontWeight='500'>
                    {formatDate(tutor.approval_date)}
                  </Text>
                </Box>
              )}
              {tutor.approval_notes && (
                <Box gridColumn={{ base: '1', md: '1 / 3' }}>
                  <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                    Approval Notes
                  </Text>
                  <Text color={textColor} fontSize='md' fontWeight='500' whiteSpace="pre-wrap">
                    {tutor.approval_notes}
                  </Text>
                </Box>
              )}
            </SimpleGrid>
          </Card>
        )}

        {/* Timestamps */}
        <Card p='30px' bg={cardBg}>
          <Text color={textColor} fontSize='xl' fontWeight='600' mb='20px'>
            Timestamps
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Created At
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(tutor.created_at)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Last Updated
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(tutor.updated_at)}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
      </Flex>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Edit Tutor
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Full Name
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Email
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Phone
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Current Year
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="current_year"
                  value={formData.current_year}
                  onChange={handleInputChange}
                  placeholder="Enter current year"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Academic Field
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="academic_field"
                  value={formData.academic_field}
                  onChange={handleInputChange}
                  placeholder="Enter academic field"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Department of Choice
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="department_of_choice"
                  value={formData.department_of_choice}
                  onChange={handleInputChange}
                  placeholder="Enter department of choice"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Experience at MIA
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="experience_at_mia"
                  value={formData.experience_at_mia}
                  onChange={handleInputChange}
                  placeholder="Enter experience at MIA"
                  mb='8px'
                />
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Why Choose You
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  name="why_choose_you"
                  value={formData.why_choose_you}
                  onChange={handleInputChange}
                  placeholder="Enter why choose you"
                  rows={4}
                  mb='8px'
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isLoading={updating}
              loadingText="Updating">
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              Delete Tutor
            </AlertDialogHeader>
            <AlertDialogBody color={textColorSecondary}>
              Are you sure you want to delete this tutor application? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        isOpen={isApproveOpen}
        leastDestructiveRef={cancelRef}
        onClose={onApproveClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              Approve Tutor Application
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl mb="20px">
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Approval Notes (Optional)
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Enter approval notes (optional)"
                  rows={3}
                />
              </FormControl>
              <Text color={textColorSecondary} fontSize="sm">
                Are you sure you want to approve this tutor application?
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onApproveClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleApprove}
                ml={3}
                isLoading={approving}
                loadingText="Approving">
                Approve
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRejectClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              Reject Tutor Application
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl mb="20px">
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Rejection Notes (Optional)
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Enter rejection notes (optional)"
                  rows={3}
                />
              </FormControl>
              <Text color={textColorSecondary} fontSize="sm">
                Are you sure you want to reject this tutor application?
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRejectClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleReject}
                ml={3}
                isLoading={rejecting}
                loadingText="Rejecting">
                Reject
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

